import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { entryHours, fmtDur } from '../../lib/time.js';
import { workTypeLabel, workTypeOf } from '../../lib/workTypes.js';
import {
  buildClientSections,
  buildProjectSections,
  clientRowValue,
  sortClientRows,
} from './reportUtils.js';
import {
  Avatar,
  Card,
  flashRowProps,
  Progress,
  ProjectLabel,
  Table,
  useSortedRows,
} from '../ui.jsx';

const PROJECT_HEADERS = [
  { label: 'Project', sortKey: 'project' },
  { label: 'Client', sortKey: 'client' },
  { label: 'Work type', sortKey: 'workType' },
  { label: 'Hours', sortKey: 'hours' },
  { label: 'MD', sortKey: 'md' },
  { label: 'People', sortKey: 'people' },
  { label: 'Share', sortKey: 'share' },
];
// The same split as the hour boxes above, per person: Total (work + paid
// absence) and MD, then each work type, then client work share of work.
const PEOPLE_HEADERS = [
  { label: 'Person', sortKey: 'person' },
  { label: 'Team', sortKey: 'team' },
  { label: 'Total', sortKey: 'hours' },
  { label: 'MD', sortKey: 'md' },
  { label: 'Client work', sortKey: 'client' },
  { label: 'Internal work', sortKey: 'internal' },
  { label: 'Paid absence', sortKey: 'paidAbsence' },
  { label: 'Unpaid absence', sortKey: 'unpaidAbsence' },
  { label: 'Client share', sortKey: 'clientWork' },
  { label: 'Share', sortKey: 'share' },
];
const CLIENT_HEADERS = [
  { label: 'Client', sortKey: 'client' },
  { label: 'Hours', sortKey: 'hours' },
  { label: 'MD', sortKey: 'md' },
  { label: 'Client work', sortKey: 'clientWork' },
  { label: 'Projects', sortKey: 'projects' },
  { label: 'Share', sortKey: 'share' },
];
const ENTRY_HEADERS = [
  { label: 'Date', sortKey: 'date' },
  { label: 'Person', sortKey: 'person' },
  { label: 'Project', sortKey: 'project' },
  { label: 'Client', sortKey: 'client' },
  { label: 'Work type', sortKey: 'workType' },
  'Description',
  { label: 'Duration', sortKey: 'duration' },
];

export function SummaryReport({
  summary,
  projectRows,
  clientRows,
  peopleRows,
}) {
  return (
    <div className="report-summary-grid">
      <Card title="Top projects">
        {projectRows
          .filter((row) => projectWorkType(row) !== 'unpaid-absence')
          .slice(0, 5)
          .map((row) => (
            <SummaryLine
              key={row.project.id}
              label={<ProjectLabel project={row.project} />}
              value={fmtDur(row.hours)}
              pct={(row.hours / Math.max(1, summary.totalHours)) * 100}
            />
          ))}
      </Card>
      <Card title="Top clients">
        {clientRows.slice(0, 5).map((row) => (
          <SummaryLine
            key={row.client.id}
            label={row.client.name}
            value={fmtDur(row.hours)}
            pct={(row.hours / Math.max(1, summary.totalHours)) * 100}
          />
        ))}
      </Card>
      <Card title="Top people">
        {peopleRows.slice(0, 5).map((row) => (
          <SummaryLine
            key={row.user.id}
            label={row.user.name}
            value={fmtDur(row.hours)}
            pct={(row.hours / Math.max(1, summary.totalHours)) * 100}
          />
        ))}
      </Card>
    </div>
  );
}

function projectWorkType(row) {
  return row.workType || workTypeOf(row.project);
}

function projectRowValue(row, key) {
  if (key === 'project') return `${row.project.name} ${row.project.code}`;
  if (key === 'client') return row.project.clientName || '';
  if (key === 'workType') return workTypeLabel(projectWorkType(row));
  if (key === 'people') return row.people?.size ?? row.peopleCount;
  return row.hours;
}

export function ProjectsReport({
  rows,
  total,
  onOpenProject,
  canOpenProject = () => false,
  persistKey,
}) {
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [sortedRows, sort, sortBy, flashToken] = useSortedRows(
    rows,
    projectRowValue,
    { key: 'hours', direction: 'desc' },
    persistKey && `${persistKey}.projects`,
    PROJECT_HEADERS,
  );
  const sections = buildProjectSections(sortedRows);

  function toggleSection(id) {
    setCollapsedSections((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Table headers={PROJECT_HEADERS} sort={sort} onSort={sortBy}>
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          <ProjectSectionRow
            section={section}
            total={total}
            expanded={!collapsedSections.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
          {!collapsedSections.has(section.id) && (
            <ProjectRows
              rows={section.rows}
              total={total}
              flashToken={flashToken}
              onOpenProject={onOpenProject}
              canOpenProject={canOpenProject}
            />
          )}
        </React.Fragment>
      ))}
    </Table>
  );
}

function ProjectRows({
  rows,
  total,
  flashToken,
  onOpenProject,
  canOpenProject,
}) {
  return rows.map((row) => {
    const { project, hours, people, peopleCount } = row;
    const { key, className } = flashRowProps(project.id, flashToken);
    const navigable = canOpenProject(project.id);
    return (
      <tr key={key} className={className}>
        <td>
          {navigable ? (
            <button
              type="button"
              className="report-entity-link"
              onClick={() => onOpenProject(project.id)}
            >
              <ProjectLabel project={project} />
            </button>
          ) : (
            <ProjectLabel project={project} />
          )}
        </td>
        <td>{project.clientName}</td>
        <td>{workTypeLabel(projectWorkType(row))}</td>
        <td className="strong">{fmtDur(hours)}</td>
        <td>{(hours / 8).toFixed(2)}</td>
        <td>{people?.size ?? peopleCount}</td>
        <td>
          {projectWorkType(row) === 'unpaid-absence' ? (
            <NotInTotal />
          ) : (
            <Progress pct={(hours / Math.max(1, total)) * 100} />
          )}
        </td>
      </tr>
    );
  });
}

// Unpaid absence is not part of the total a share is taken of, so its rows
// and section say so instead of claiming a share of it.
const UNCOUNTED_SECTION = 'unpaid-absence';

function NotInTotal() {
  return <span className="muted-cell">not in total</span>;
}

function ProjectSectionRow({ section, total, expanded, onToggle }) {
  const share = (section.hours / Math.max(1, total)) * 100;
  return (
    <tr className="client-section-row project-section-row">
      <td>
        <button
          type="button"
          className="client-section-label client-section-toggle"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${section.label}`}
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          <strong>{section.label}</strong>
          <span>
            {section.rows.length} project
            {section.rows.length === 1 ? '' : 's'}
          </span>
        </button>
      </td>
      <td />
      <td />
      <td className="strong">{fmtDur(section.hours)}</td>
      <td>{(section.hours / 8).toFixed(2)}</td>
      <td />
      <td>
        {section.id === UNCOUNTED_SECTION ? (
          <NotInTotal />
        ) : (
          <div className="client-section-share">
            <Progress pct={share} />
            <span>{share.toFixed(0)}%</span>
          </div>
        )}
      </td>
    </tr>
  );
}

function peopleRowValue(row, key) {
  if (key === 'person') return row.user.name;
  if (key === 'team') return row.user.teamName || '';
  if (key === 'client') return row.billableHours || 0;
  if (key === 'internal') return row.internalHours || 0;
  if (key === 'paidAbsence') return row.paidAbsenceHours || 0;
  if (key === 'unpaidAbsence') return row.unpaidAbsenceHours || 0;
  if (key === 'clientWork')
    return row.workHours ? row.billableHours / row.workHours : 0;
  return row.hours;
}

export function PeopleReport({
  rows,
  total,
  onOpenUser,
  canOpenUser = () => false,
  persistKey,
}) {
  const [sortedRows, sort, sortBy, flashToken] = useSortedRows(
    rows,
    peopleRowValue,
    { key: 'hours', direction: 'desc' },
    persistKey && `${persistKey}.people`,
    PEOPLE_HEADERS,
  );
  return (
    <Table headers={PEOPLE_HEADERS} sort={sort} onSort={sortBy}>
      {sortedRows.map(
        ({
          user,
          hours,
          workHours,
          billableHours,
          internalHours,
          paidAbsenceHours,
          unpaidAbsenceHours,
        }) => {
          const { key, className } = flashRowProps(user.id, flashToken);
          const navigable = canOpenUser(user.id);
          return (
            <tr key={key} className={className}>
              <td>
                {navigable ? (
                  <button
                    type="button"
                    className="report-entity-link person"
                    onClick={() => onOpenUser(user.id)}
                  >
                    <Avatar user={user} />
                    <span>{user.name}</span>
                  </button>
                ) : (
                  <div className="person">
                    <Avatar user={user} />
                    <span>{user.name}</span>
                  </div>
                )}
              </td>
              <td>{user.teamName || '-'}</td>
              <td className="strong">{fmtDur(hours)}</td>
              <td>{(hours / 8).toFixed(2)}</td>
              <td>{billableHours ? fmtDur(billableHours) : '-'}</td>
              <td>{internalHours ? fmtDur(internalHours) : '-'}</td>
              <td>{paidAbsenceHours ? fmtDur(paidAbsenceHours) : '-'}</td>
              <td className="muted-cell">
                {unpaidAbsenceHours ? fmtDur(unpaidAbsenceHours) : '-'}
              </td>
              <td>
                {workHours ? ((billableHours / workHours) * 100).toFixed(0) : 0}
                %
              </td>
              <td>
                <Progress pct={(hours / Math.max(1, total)) * 100} />
              </td>
            </tr>
          );
        },
      )}
    </Table>
  );
}

export function ClientsReport({
  rows,
  projectRows,
  total,
  onOpenClient,
  canOpenClient = () => false,
  persistKey,
}) {
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [sortedRows, sort, sortBy, flashToken] = useSortedRows(
    rows,
    clientRowValue,
    { key: 'hours', direction: 'desc' },
    persistKey && `${persistKey}.clients`,
    CLIENT_HEADERS,
  );
  const sections = buildClientSections(sortedRows, projectRows).map(
    (section) =>
      section.subsections
        ? {
            ...section,
            subsections: section.subsections.map((subsection) => ({
              ...subsection,
              rows: sortClientRows(subsection.rows, sort),
            })),
          }
        : section,
  );

  function toggleSection(id) {
    setCollapsedSections((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Table headers={CLIENT_HEADERS} sort={sort} onSort={sortBy}>
      {sections.map((section) => (
        <React.Fragment key={section.id}>
          <ClientSectionRow
            section={section}
            total={total}
            expanded={!collapsedSections.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          />
          {!collapsedSections.has(section.id) && section.subsections ? (
            section.subsections.map((subsection) => (
              <React.Fragment key={subsection.id}>
                <ClientSectionRow
                  section={subsection}
                  total={total}
                  subsection
                  expanded={!collapsedSections.has(subsection.id)}
                  onToggle={() => toggleSection(subsection.id)}
                />
                {!collapsedSections.has(subsection.id) && (
                  <ClientRows
                    rows={subsection.rows}
                    total={total}
                    flashToken={flashToken}
                    keyPrefix={subsection.id}
                    subsection
                    onOpenClient={onOpenClient}
                    canOpenClient={canOpenClient}
                  />
                )}
              </React.Fragment>
            ))
          ) : !collapsedSections.has(section.id) ? (
            <ClientRows
              rows={section.rows}
              total={total}
              flashToken={flashToken}
              keyPrefix={section.id}
              uncounted={section.id === UNCOUNTED_SECTION}
              onOpenClient={onOpenClient}
              canOpenClient={canOpenClient}
            />
          ) : null}
        </React.Fragment>
      ))}
    </Table>
  );
}

function ClientRows({
  rows,
  total,
  flashToken,
  keyPrefix,
  subsection = false,
  uncounted = false,
  onOpenClient,
  canOpenClient,
}) {
  return rows.map(
    ({ client, hours, billableHours, projects, projectCount }) => {
      const { key, className } = flashRowProps(
        `${keyPrefix}:${client.id}`,
        flashToken,
        subsection ? 'client-subsection-item' : '',
      );
      const navigable = canOpenClient(client.id);
      return (
        <tr key={key} className={className}>
          <td className="strong">
            {navigable ? (
              <button
                type="button"
                className="report-entity-link"
                onClick={() => onOpenClient(client.id)}
              >
                {client.name}
              </button>
            ) : (
              client.name
            )}
          </td>
          <td className="strong">{fmtDur(hours)}</td>
          <td>{(hours / 8).toFixed(2)}</td>
          <td>{hours ? ((billableHours / hours) * 100).toFixed(0) : 0}%</td>
          <td>{projects?.size ?? projectCount}</td>
          <td>
            {uncounted ? (
              <NotInTotal />
            ) : (
              <Progress pct={(hours / Math.max(1, total)) * 100} />
            )}
          </td>
        </tr>
      );
    },
  );
}

function ClientSectionRow({
  section,
  total,
  subsection = false,
  expanded,
  onToggle,
}) {
  const count = section.rows.length;
  const share = (section.hours / Math.max(1, total)) * 100;
  return (
    <tr
      className={`client-section-row${subsection ? ' client-subsection-row' : ''}`}
    >
      <td>
        <button
          type="button"
          className="client-section-label client-section-toggle"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${section.label}`}
          onClick={onToggle}
        >
          {expanded ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
          <strong>{section.label}</strong>
          <span>
            {count} {section.itemLabel}
            {count === 1 ? '' : 's'}
          </span>
        </button>
      </td>
      <td className="strong">{fmtDur(section.hours)}</td>
      <td>{(section.hours / 8).toFixed(2)}</td>
      <td>
        {section.hours
          ? ((section.billableHours / section.hours) * 100).toFixed(0)
          : 0}
        %
      </td>
      <td>{section.projectCount}</td>
      <td>
        {section.id === UNCOUNTED_SECTION ? (
          <NotInTotal />
        ) : (
          <div className="client-section-share">
            <Progress pct={share} />
            <span>{share.toFixed(0)}%</span>
          </div>
        )}
      </td>
    </tr>
  );
}

export function EntriesReport({ rows, data, persistKey }) {
  function entryRowValue(entry, key) {
    const project = data.allProjects.find(
      (item) => item.id === entry.projectId,
    );
    if (key === 'date')
      return new Date(entry.workDate).getTime() + entry.start * 3600000;
    if (key === 'person') return entry.userName;
    if (key === 'project')
      return project ? `${project.name} ${project.code}` : '';
    if (key === 'client') return project?.clientName || '';
    if (key === 'workType') return workTypeLabel(workTypeOf(project, entry));
    return entryHours(entry);
  }
  const [sortedRows, sort, sortBy, flashToken] = useSortedRows(
    rows,
    entryRowValue,
    { key: 'date', direction: 'desc' },
    persistKey && `${persistKey}.entries`,
    ENTRY_HEADERS,
  );
  return (
    <Table headers={ENTRY_HEADERS} sort={sort} onSort={sortBy}>
      {sortedRows.map((entry) => {
        const project = data.allProjects.find(
          (item) => item.id === entry.projectId,
        );
        const { key, className } = flashRowProps(entry.id, flashToken);
        return (
          <tr key={key} className={className}>
            <td>{entry.workDate}</td>
            <td>{entry.userName}</td>
            <td>
              <ProjectLabel project={project} />
            </td>
            <td>{project?.clientName || '-'}</td>
            <td>{workTypeLabel(workTypeOf(project, entry))}</td>
            <td>
              <span className="truncate table-description">
                {entry.description}
              </span>
            </td>
            <td className="strong">{fmtDur(entryHours(entry))}</td>
          </tr>
        );
      })}
    </Table>
  );
}

function SummaryLine({ label, value, pct }) {
  return (
    <div className="summary-line">
      <span>{label}</span>
      <strong>{value}</strong>
      <Progress pct={pct} />
    </div>
  );
}
